import { OpenAccessPassSeatHelperFactory, OpenAccessPassSeatSource, SeatedPassProtocol } from '../../../../utility/open-access-pass-seat-helper-factory.js';
import { isSameData, isSameMap } from '../../../dto/data-model-utils.js';
import { EventOpenAccessProtocol } from '../../open-access-pass/event-open-access-protocol.js';
import { OpenAccessPass } from '../../open-access-pass/open-access-pass.js';
import { SeatedOpenAccessProtocol } from '../../open-access-pass/seated-open-access-protocol.js';
import { StagedSeatedOpenAccessProtocol } from '../../open-access-pass/staged-seated-open-access-pass-protocol.js';
import { OpenAccessPassModifiedMaxCountEntry } from '../audit-log-entries.js';
import { AuditLogEntry } from '../audit-log-entry.js';
import { OpenAccessPassModifiedEventsEntry } from '../passes/open-access-pass-modified-events-entry.js';
import { SeatedOpenAccessPassMovedEntry } from '../passes/seated-open-access-pass-moved-entry.js';
import { SeatedOpenAccessPassSwappedEntry, SeatRef } from '../passes/seated-open-access-pass-swap-entry.js';

export function* generateAuditLogEntriesForUpdatedEventOpenAccessPass(
    oldPass: OpenAccessPass,
    updatedPass: OpenAccessPass,
): IterableIterator<AuditLogEntry> {
  yield* didChangeSeatId(oldPass, updatedPass);
  yield* didChangeEvents(oldPass, updatedPass);
  yield* didSwapSeats(oldPass, updatedPass);
  yield* didChangeMaxCount(oldPass, updatedPass);
}

function* didChangeSeatId(oldPass: OpenAccessPass, updatedPass: OpenAccessPass): Generator<SeatedOpenAccessPassMovedEntry> {
  const sourcePairs = pairedSeatSources(oldPass, updatedPass);
  if(sourcePairs == null) return;

  for(const [oldSource, newSource] of sourcePairs) {
    const oldSeatId = oldSource.seatingOptions?.seatId ?? null;
    const newSeatId = newSource.seatingOptions?.seatId ?? null;
    if(oldSeatId === newSeatId) continue;

    yield SeatedOpenAccessPassMovedEntry.create({
      before: oldPass,
      after: updatedPass,
      oldSeatId,
      newSeatId,
    });
  }
}

function* didChangeEvents(
    oldPass: OpenAccessPass,
    updatedPass: OpenAccessPass
): Generator<OpenAccessPassModifiedEventsEntry> {
  const oldProtocol = oldPass.protocol;
  const newProtocol = updatedPass.protocol;
  if(isSeatedPassProtocol(oldProtocol) && isSeatedPassProtocol(newProtocol)) {
    const addedEvents = new Set<string>();
    const removedEvents = new Set<string>();

    for(const [oldSource, newSource] of pairedSeatSources(oldPass, updatedPass) ?? []) {
      const candidateEventIds = new Set([
        ...oldSource.eventConfiguration.keys(),
        ...newSource.eventConfiguration.keys(),
        ...oldSource.modifiers.keys(),
        ...newSource.modifiers.keys(),
      ]);
      for(const eventId of candidateEventIds) {
        const wasAssigned = oldSource.helper.getSeatForEvent(eventId) != null;
        const isAssigned = newSource.helper.getSeatForEvent(eventId) != null;
        if(!wasAssigned && isAssigned) addedEvents.add(eventId);
        if(wasAssigned && !isAssigned) removedEvents.add(eventId);
      }
    }

    if(addedEvents.size > 0 || removedEvents.size > 0) {
      yield OpenAccessPassModifiedEventsEntry.create({
        before: oldPass,
        after: updatedPass,
        addedEvents: [...addedEvents],
        removedEvents: [...removedEvents],
      });
    }
    return;
  }

  if(!EventOpenAccessProtocol.assertType(oldProtocol) || !EventOpenAccessProtocol.assertType(newProtocol)) {
    return;
  }

  const candidateEventIds = new Set([
    ...oldProtocol.eventConfiguration.keys(),
    ...newProtocol.eventConfiguration.keys(),
  ]);
  const oldVariationId = oldPass.getVariationIdOrDefault();
  const newVariationId = updatedPass.getVariationIdOrDefault();
  const addedEvents: string[] = [];
  const removedEvents: string[] = [];
  for(const eventId of candidateEventIds) {
    const wasAssigned = oldProtocol.isEventAssignedToPass({ eventId, passVariationId: oldVariationId });
    const isAssigned = newProtocol.isEventAssignedToPass({ eventId, passVariationId: newVariationId });

    if(!wasAssigned && isAssigned) addedEvents.push(eventId);
    if(wasAssigned && !isAssigned) removedEvents.push(eventId);
  }

  const didAddOrRemoveEvents = addedEvents.length > 0 || removedEvents.length > 0;
  if(!didAddOrRemoveEvents) {
    return;
  }

  yield OpenAccessPassModifiedEventsEntry.create({
    before: oldPass,
    after: updatedPass,
    addedEvents: addedEvents,
    removedEvents: removedEvents,
  });
}

function* didSwapSeats(
    oldPass: OpenAccessPass,
    updatedPass: OpenAccessPass
): Generator<SeatedOpenAccessPassSwappedEntry> {
  if(isSeatedPassProtocol(oldPass.protocol) && isSeatedPassProtocol(updatedPass.protocol)) {
    const addedSeatRefs: SeatRef[] = [];
    const removedSeatRefs: SeatRef[] = [];

    for(const [oldSource, newSource] of pairedSeatSources(oldPass, updatedPass) ?? []) {
      if(isSameMap(oldSource.modifiers, newSource.modifiers, isSameData)) continue;

      const candidateEventIds = new Set([...oldSource.modifiers.keys(), ...newSource.modifiers.keys()]);
      for(const eventId of candidateEventIds) {
        const oldSeatId = oldSource.helper.getSeatForEvent(eventId)?.seatId ?? null;
        const newSeatId = newSource.helper.getSeatForEvent(eventId)?.seatId ?? null;
        if(oldSeatId === newSeatId) continue;

        if(oldSeatId != null) removedSeatRefs.push(SeatRef.create({ eventId, seatId: oldSeatId }));
        if(newSeatId != null) addedSeatRefs.push(SeatRef.create({ eventId, seatId: newSeatId }));
      }
    }

    if(addedSeatRefs.length > 0 || removedSeatRefs.length > 0) {
      yield SeatedOpenAccessPassSwappedEntry.create({
        before: oldPass,
        after: updatedPass,
        addedSeats: addedSeatRefs,
        removedSeats: removedSeatRefs,
      });
    }
    return;
  }
}

function* didChangeMaxCount(
    oldPass: OpenAccessPass,
    updatedPass: OpenAccessPass,
): Generator<OpenAccessPassModifiedMaxCountEntry> {
  const isPassProtocolRelevant =
    (SeatedOpenAccessProtocol.assertType(oldPass.protocol) && SeatedOpenAccessProtocol.assertType(updatedPass.protocol)) ||
    (StagedSeatedOpenAccessProtocol.assertType(oldPass.protocol) && StagedSeatedOpenAccessProtocol.assertType(updatedPass.protocol));
  if(!isPassProtocolRelevant) {
    return;
  }

  const oldMaxCount = oldPass.protocol.maxEventCount;
  const newMaxCount = updatedPass.protocol.maxEventCount;

  const isSameMaxCount = oldMaxCount.isSame(newMaxCount, isSameData);
  if(isSameMaxCount) {
    return;
  }

  yield OpenAccessPassModifiedMaxCountEntry.create({
    oldMaxCount,
    newMaxCount,
    before: oldPass,
    after: updatedPass,
  });
}

function pairedSeatSources(
    oldPass: OpenAccessPass,
    updatedPass: OpenAccessPass,
): [OpenAccessPassSeatSource, OpenAccessPassSeatSource][] | null {
  if(!isSeatedPassProtocol(oldPass.protocol) || !isSeatedPassProtocol(updatedPass.protocol)) {
    return null;
  }

  const oldSources = OpenAccessPassSeatHelperFactory.forPass({
    protocol: oldPass.protocol,
    passVariationId: oldPass.getVariationIdOrDefault(),
  });
  const newSources = OpenAccessPassSeatHelperFactory.forPass({
    protocol: updatedPass.protocol,
    passVariationId: updatedPass.getVariationIdOrDefault(),
  });
  const newSourcesById = new Map(newSources.map((source) => [source.sourceId, source]));

  const result: [OpenAccessPassSeatSource, OpenAccessPassSeatSource][] = [];
  for(const oldSource of oldSources) {
    const newSource = newSourcesById.get(oldSource.sourceId);
    if(newSource != null) result.push([oldSource, newSource]);
  }
  return result;
}

function isSeatedPassProtocol(protocol: OpenAccessPass['protocol']): protocol is SeatedPassProtocol {
  return protocol instanceof SeatedOpenAccessProtocol || protocol instanceof StagedSeatedOpenAccessProtocol;
}
